import type { FocusEventHandler } from 'react'
import { useState } from 'react'

import { useLingui } from '@lingui/react'
import { clamp, isNil } from 'ramda'
import type {
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'
import type {
  NumberFormatBaseProps,
  NumericFormatProps,
  OnValueChange,
} from 'react-number-format'
import { NumberFormatBase, useNumericFormat } from 'react-number-format'

import type { I18nLanguage } from '@twisto/base'
import { useAppConfig } from '@twisto/base'

const DEFAULT_MAX = 2147483647
const DEFAULT_MIN = 0
const FRACTION_DIGITS = 2

type Props = {
  name: string
  max?: number
  min?: number
  rules?: ControllerProps['rules']
} & Omit<
  NumericFormatProps,
  | 'allowNegative'
  | 'allowedDecimalSeparators'
  | 'decimalScale'
  | 'decimalSeparator'
  | 'thousandSeparator'
  | 'thousandsGroupStyle'
  | 'onValueChange'
  | 'onChange'
  | 'getInputRef'
  | 'name'
  | 'value'
>

export const CurrencyInputBase = ({
  max = DEFAULT_MAX,
  min = DEFAULT_MIN,
  onBlur: customOnBlur,
  name: fieldName,
  rules,
  ...restProps
}: Props) => {
  const { watch, setValue, control } = useFormContext()

  const [didValueChange, setDidValueChange] = useState(false)

  const { i18n } = useLingui()
  const locale = i18n.locale as I18nLanguage

  const { formattedOptions } = useAppConfig()

  const { decimalSeparator, thousandSeparator } =
    formattedOptions.currency[locale]

  const { format, ...rest } = useNumericFormat({
    allowNegative: false,
    allowedDecimalSeparators: [',', '.'],
    decimalScale: FRACTION_DIGITS,
    decimalSeparator,
    thousandSeparator,
    thousandsGroupStyle: 'thousand',
    ...restProps,
  })

  const handleFormat: NumberFormatBaseProps['format'] = (value) => {
    let formattedValue = format(value)

    if (didValueChange && formattedValue === '') {
      formattedValue = '0'
    }

    return formattedValue
  }

  const handleGetCaretBoundary: NumberFormatBaseProps['getCaretBoundary'] = (
    formattedValue
  ) =>
    Array.from({ length: formattedValue.length + 1 }).map(
      (_, index) => !(index === 0 && formattedValue === '0')
    )

  type ControllerField = ControllerRenderProps<FieldValues, string>

  const handleOnBlur =
    (
      name: ControllerField['name'],
      value: ControllerField['value'],
      onBlur: ControllerField['onBlur']
    ): FocusEventHandler<HTMLInputElement> =>
    (event) => {
      onBlur()
      !isNil(customOnBlur) && customOnBlur(event)

      const oldValue = watch(name)
      const clampedValue = clamp(min, max, Number(value)).toString()

      if (clampedValue !== oldValue) {
        setValue(name, clampedValue)
      }
    }

  const handleOnValueChange =
    (
      value: ControllerField['value'],
      onChange: ControllerField['onChange']
    ): OnValueChange =>
    (values) => {
      if (!didValueChange) {
        setDidValueChange(true)
      }

      const oldValue = (value ?? 0).toString()
      const newValue = (
        values.floatValue === undefined ? 0 : values.floatValue
      ).toString()

      if (newValue !== oldValue) {
        onChange({ target: { value: newValue } })
      }
    }

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { onChange, onBlur, name, value, ref } }) => (
        <NumberFormatBase
          data-testid="currency-input-base"
          {...rest}
          format={handleFormat}
          getCaretBoundary={handleGetCaretBoundary}
          getInputRef={ref}
          name={name}
          type="tel"
          value={value}
          valueIsNumericString={true}
          onBlur={handleOnBlur(name, value, onBlur)}
          onValueChange={handleOnValueChange(value, onChange)}
        />
      )}
      rules={rules}
    />
  )
}
